import { useEffect, useState } from 'react';

import { Badge, Button } from '@ds/core';
import { Alert } from '@ds/feedback';
import { useAviso } from '@/comuns/toast';
import type { AgendaGoogle } from '@/data/agendaGoogle';
import { dados } from '@/data/porta';
import { atual } from '@/session/sessao';

const TEXTO = { fontSize: 12.5, lineHeight: '18px', color: 'var(--text-secondary)', margin: 0 } as const;

/**
 * Google Agenda na janela Editar conta (0152).
 *
 * Cada profissional conecta a própria conta: os atendimentos e bloqueios dele entram
 * na agenda principal do Google, só com o primeiro nome do paciente. Desconectar tira
 * de lá os de hoje em diante — por isso pede confirmação. Operador da plataforma e
 * impersonação não veem a seção: a conexão é da pessoa, e quem entrou para ajudar não
 * mexe nela.
 */
export function AgendaGoogleDaConta({ aberta, irPara = (url) => window.location.assign(url) }: {
    aberta: boolean;
    /** Para onde a tela vai com a URL do Google. Nos testes, um espião. */
    irPara?: (url: string) => void;
}) {
    const aviso = useAviso();
    const sessao = atual();
    const [agenda, setAgenda] = useState<AgendaGoogle | null | undefined>(undefined);
    const [erro, setErro] = useState<string | null>(null);
    const [indo, setIndo] = useState(false);
    const [confirmando, setConfirmando] = useState(false);
    const [desconectando, setDesconectando] = useState(false);

    useEffect(() => {
        if (!aberta) return undefined;
        let vivo = true;
        setAgenda(undefined);
        setErro(null);
        setConfirmando(false);
        dados().minhaAgendaGoogle()
            .then((a) => { if (vivo) setAgenda(a); })
            .catch((e: unknown) => {
                if (!vivo) return;
                setAgenda(null);
                setErro(e instanceof Error ? e.message : 'Não foi possível ler a sua agenda Google.');
            });
        return () => { vivo = false; };
    }, [aberta]);

    if (!sessao || sessao.perfil !== 'clinica' || sessao.impersonando) return null;

    const conectar = async () => {
        setErro(null);
        setIndo(true);
        try {
            const destino = window.location.pathname.startsWith('/app') ? window.location.pathname : '/app';
            irPara(await dados().conectarAgendaGoogle(destino));
        } catch (e) {
            setIndo(false);
            setErro(e instanceof Error ? e.message : 'Não foi possível abrir o Google.');
        }
    };

    const desconectar = async () => {
        setErro(null);
        setDesconectando(true);
        try {
            await dados().desconectarAgendaGoogle();
            setAgenda((a) => (a ? { ...a, situacao: 'desconectando' } : a));
            setConfirmando(false);
            aviso({ title: 'Desconectando a agenda', description: 'Os atendimentos de hoje em diante saem da sua agenda Google em alguns minutos.' });
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível desconectar.');
        } finally {
            setDesconectando(false);
        }
    };

    return (
        <section aria-label="Google Agenda" style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h3 style={{ flex: 1, margin: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>Google Agenda</h3>
                {agenda?.situacao === 'conectada' && <Badge tone="success" dot>Conectada</Badge>}
                {agenda?.situacao === 'revogada' && <Badge tone="warning" dot>Sem acesso</Badge>}
                {agenda?.situacao === 'desconectando' && <Badge tone="neutral" dot>Desconectando</Badge>}
            </div>

            {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

            {agenda === undefined && !erro && <p role="status" style={TEXTO}>Carregando…</p>}

            {agenda === null && (
                <>
                    <p style={TEXTO}>Seus atendimentos e bloqueios aparecem na sua agenda Google, só com o primeiro nome do paciente.</p>
                    <Button variant="secondary" size="sm" loading={indo} onClick={() => { void conectar(); }}>Conectar minha agenda Google</Button>
                </>
            )}

            {agenda?.situacao === 'conectada' && !confirmando && (
                <>
                    <p style={TEXTO}>
                        Conectada{agenda.email ? <> como <strong>{agenda.email}</strong></> : null}. Cada mudança aparece lá em até 5 minutos.
                    </p>
                    <Button variant="ghost" size="sm" onClick={() => setConfirmando(true)}>Desconectar</Button>
                </>
            )}

            {agenda?.situacao === 'revogada' && !confirmando && (
                <>
                    <p style={TEXTO}>O Google tirou o acesso à sua agenda. Reconecte para os atendimentos voltarem a aparecer lá.</p>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Button variant="secondary" size="sm" loading={indo} onClick={() => { void conectar(); }}>Reconectar</Button>
                        <Button variant="ghost" size="sm" onClick={() => setConfirmando(true)}>Desconectar</Button>
                    </div>
                </>
            )}

            {confirmando && (
                <>
                    <p style={TEXTO}>Os atendimentos de hoje em diante saem da sua agenda Google. Os passados ficam lá.</p>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Button size="sm" loading={desconectando} onClick={() => { void desconectar(); }}>Sim, desconectar</Button>
                        <Button variant="ghost" size="sm" onClick={() => setConfirmando(false)}>Manter conectada</Button>
                    </div>
                </>
            )}

            {agenda?.situacao === 'desconectando' && (
                <p style={TEXTO}>Tirando os atendimentos de hoje em diante da sua agenda Google. Leva alguns minutos.</p>
            )}
        </section>
    );
}
